import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { ClientResponse } from '@rs/http-client';
import type { AppRoutes, Chain, Currency } from '@rs/spec-app-api';
import { api } from '@src/shared/api';

type ConfigData = ClientResponse<AppRoutes['getConfig']>;
type PricesData = ClientResponse<AppRoutes['getPrices']>;
type UserStatusData = ClientResponse<AppRoutes['getUserStatus']>;

function pickValidCode(codes: string[], saved: string, fallback: string): string {
	if (saved && codes.includes(saved)) {
		return saved;
	}

	return fallback;
}

export const useAppConfigStore = defineStore('app-config', () => {
	const config = ref<ConfigData | null>(null);
	const chains = ref<Chain[]>([]);
	const prices = ref<null | PricesData>(null);
	const userStatus = ref<null | UserStatusData>(null);
	const selectedChainCode = ref(localStorage.getItem('rs:chain') ?? '');
	const selectedLanguageCode = ref(localStorage.getItem('rs:language') ?? '');
	const selectedCurrencyCode = ref(localStorage.getItem('rs:currency') ?? '');
	const walletAddress = ref<null | string>(null);

	const selectedChain = computed<Chain | undefined>(() =>
		chains.value.find((chain) => chain.code === selectedChainCode.value),
	);

	const selectedCurrency = computed<Currency | undefined>(() =>
		config.value?.currencies.find((cur) => cur.code === selectedCurrencyCode.value),
	);

	function persistPreferences() {
		localStorage.setItem('rs:chain', selectedChainCode.value);
		localStorage.setItem('rs:language', selectedLanguageCode.value);
		localStorage.setItem('rs:currency', selectedCurrencyCode.value);
	}

	/** Пока статус не загружен — не показываем поле рефералки (избегаем мигания). */
	const showInviteCode = computed(() => userStatus.value !== null && !userStatus.value.hasDeposited);
	const referralLink = computed(() => userStatus.value?.refLink ?? '');

	function getVaultDepositRaw(vaultId: string): string {
		return userStatus.value?.deposits[vaultId]?.raw ?? '0';
	}

	async function refreshUserStatus() {
		if (!selectedChainCode.value) {
			userStatus.value = null;
			return;
		}

		if (!walletAddress.value) {
			userStatus.value = null;
			return;
		}

		try {
			userStatus.value = await api.getUserStatus(
				{ walletAddress: walletAddress.value },
				{ headers: { 'x-rs-chain-code': selectedChainCode.value } },
			);
		} catch {
			userStatus.value = null;
		}
	}

	function selectChain(code: string) {
		selectedChainCode.value = code;
		persistPreferences();
		// Fire-and-forget: ошибки гасятся внутри refreshUserStatus.
		void refreshUserStatus();
	}

	function selectLanguage(code: string) {
		selectedLanguageCode.value = code;
		persistPreferences();
	}

	function selectCurrency(code: string) {
		selectedCurrencyCode.value = code;
		persistPreferences();
	}

	function setWalletAddress(address: null | string) {
		walletAddress.value = address;
		// Fire-and-forget: ошибки гасятся внутри refreshUserStatus.
		void refreshUserStatus();
	}

	async function init() {
		const [configResult, chainsResult, pricesResult] = await Promise.all([
			api.getConfig(),
			api.listChains(),
			api.getPrices(),
		]);

		config.value = configResult;
		chains.value = chainsResult.chains;
		prices.value = pricesResult;

		const defaultChainCode = chains.value[0]?.code ?? '';
		const defaultLanguageCode = configResult.languages[0]?.code ?? '';
		const defaultCurrencyCode = configResult.currencies[0]?.code ?? '';

		selectedChainCode.value = pickValidCode(
			chainsResult.chains.map((chain) => chain.code),
			selectedChainCode.value,
			defaultChainCode,
		);
		selectedLanguageCode.value = pickValidCode(
			configResult.languages.map((lang) => lang.code),
			selectedLanguageCode.value,
			defaultLanguageCode,
		);
		selectedCurrencyCode.value = pickValidCode(
			configResult.currencies.map((cur) => cur.code),
			selectedCurrencyCode.value,
			defaultCurrencyCode,
		);

		persistPreferences();

		await refreshUserStatus();
	}

	return {
		config,
		prices,
		referralLink,
		showInviteCode,
		selectedChainCode,
		selectedLanguageCode,
		selectedCurrencyCode,
		selectedChain,
		selectedCurrency,
		chains,
		selectChain,
		selectLanguage,
		selectCurrency,
		setWalletAddress,
		init,
		getVaultDepositRaw,
	};
});
